<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Transferencias',
  },
});

const totalTransfers = computed(() => props.data?.total_transfers || 0);
const transferRate = computed(() => props.data?.transfer_rate || 0);
const byAgent = computed(() => props.data?.by_agent || []);

const sortedByTransferredOut = computed(() => {
  return [...byAgent.value].sort(
    (a, b) => b.transferred_out - a.transferred_out
  );
});

const sortedByReceived = computed(() => {
  return [...byAgent.value].sort((a, b) => b.received - a.received);
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-repeat text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary stats -->
      <div class="grid grid-cols-2 gap-3">
        <div class="text-center p-3 rounded-lg bg-n-amber-3/50">
          <div class="text-2xl font-bold text-n-amber-11">
            {{ totalTransfers }}
          </div>
          <div class="text-xs text-n-slate-11">Total Transferencias</div>
        </div>
        <div class="text-center p-3 rounded-lg bg-n-blue-3/50">
          <div class="text-2xl font-bold text-n-blue-11">
            {{ transferRate }}%
          </div>
          <div class="text-xs text-n-slate-11">Taxa de Transferencia</div>
        </div>
      </div>

      <!-- Top transferrers -->
      <div v-if="sortedByTransferredOut.length > 0">
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-arrow-right-circle text-sm text-n-amber-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Mais Transferem</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="agent in sortedByTransferredOut.slice(0, 5)"
            :key="`out-${agent.agent_id}`"
            class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1"
          >
            <Avatar :name="agent.name" :size="24" rounded-full />
            <span class="flex-1 text-sm text-n-slate-12 truncate">{{
              agent.name
            }}</span>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded bg-n-amber-3 text-n-amber-11"
            >
              {{ agent.transferred_out }} saida
            </span>
          </div>
        </div>
      </div>

      <!-- Top receivers -->
      <div v-if="sortedByReceived.length > 0">
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-arrow-left-circle text-sm text-n-teal-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Mais Recebem</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="agent in sortedByReceived.slice(0, 5)"
            :key="`in-${agent.agent_id}`"
            class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1"
          >
            <Avatar :name="agent.name" :size="24" rounded-full />
            <span class="flex-1 text-sm text-n-slate-12 truncate">{{
              agent.name
            }}</span>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded bg-n-teal-3 text-n-teal-11"
            >
              {{ agent.received }} entrada
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
